import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * The input element is a form control used to gather user input. Allowed input types are:
 * - text
 * - password
 * - number
 * - email
 * - tel
 *
 * TbtkInputComponent component accepts [title], [header], [quoata], [bottom] properties which are the content to be displayed in the sidenav.
 *
 * <sub>input.component.ts</sub>
 *
 * ```ts
 * @Input() type = 'text' | 'password' | 'number' | 'email' | 'tel' - The type of input element.
 * @Input() placeholder - The placeholder of the input element.
 * @Input() prepend - The text to prepend to the input element.
 * @Input() append - The text to append to the input element.
 * @Input() disabled - Whether the input element is disabled.
 * @Output() onChange - Line is creating an output property called `onChange` of type `EventEmitter<string>`.
 * @Output() onFocus - Line is creating an output property called `onFocus` of type `EventEmitter<string>`.
 * @Output() onBlur - Line is creating an output property called `onBlur` of type `EventEmitter<string>`.
 * @Output() onEnter - Line is creating an output property called `onEnter` of type `EventEmitter<string>`.
 * @Output() onClick - Line is creating an output property called `onClick` of type `EventEmitter<string>`.
 * ```
 * <sub>app.component.html</sub>
 *
 * Add the below code to your code stack and give initializer parameters.
 *
 * ```html
 * <tbtk-input type="text" append=".com" (onChange)="onChange.emit($event)" (onBlur)="onBlur.emit($event)" (onFocus)="onFocus.emit($event)">
 *  <i left>icon</i>
 *  <i right>icon</i>
 * </tbtk-input>
 * ```
 *
 * If you want to put icon inside of the input element, you can add the `left` and `right` attributes to your element that shows icon. Example is given above code.
 */

type Types = 'text' | 'password' | 'number' | 'email' | 'tel';

@Component({
  selector: 'tbtk-input',
  templateUrl: 'input.component.html',
  styleUrls: ['input.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class TbtkInputComponent {
  @Input() prepend = '';
  @Input() append = '';
  @Input() type: Types = 'text';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() value = '';
  @Output() outputChange = new EventEmitter<string>();
  @Output() outputFocus = new EventEmitter<string>();
  @Output() outputBlur = new EventEmitter<string>();
  @Output() outputEnter = new EventEmitter<string>();
  @Output() outputClick = new EventEmitter<string>();
}
